"use client";

import { useEffect } from "react";
import { usePathname, useSearchParams } from "next/navigation";

export const formScrollKey = (pathname: string) => `lotlift:form-scroll:${pathname}`;
type ScrollStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type ScrollTarget = { scrollTo: (options: ScrollToOptions) => void };
export type FormScrollPosition = { x: number; y: number };

export function saveFormScrollPosition(storage: ScrollStorage, pathname: string, position: FormScrollPosition): void {
  storage.setItem(formScrollKey(pathname), JSON.stringify(position));
}

export function restoreFormScrollPosition(storage: ScrollStorage, pathname: string, target: ScrollTarget): boolean {
  const saved = storage.getItem(formScrollKey(pathname));
  if (!saved) return false;
  try {
    const position = JSON.parse(saved) as FormScrollPosition;
    if (!Number.isFinite(position.x) || !Number.isFinite(position.y)) throw new Error("Invalid scroll position");
    target.scrollTo({ left: position.x, top: position.y });
    storage.removeItem(formScrollKey(pathname));
    return true;
  } catch {
    storage.removeItem(formScrollKey(pathname));
    return false;
  }
}

export function FormScrollRestoration() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const onSubmit = (event: SubmitEvent) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || form.method.toLowerCase() === "get") return;
      saveFormScrollPosition(sessionStorage, window.location.pathname, { x: window.scrollX, y: window.scrollY });
    };
    document.addEventListener("submit", onSubmit, true);
    return () => document.removeEventListener("submit", onSubmit, true);
  }, []);

  useEffect(() => {
    if (!sessionStorage.getItem(formScrollKey(pathname))) return;
    requestAnimationFrame(() => requestAnimationFrame(() => {
      restoreFormScrollPosition(sessionStorage, pathname, window);
    }));
  }, [pathname, searchParams]);

  return null;
}

export function hasPendingFormScrollRestoration(): boolean {
  return typeof window !== "undefined" && Boolean(sessionStorage.getItem(formScrollKey(window.location.pathname)));
}
